import Loading from "../components/Loading";
import OrgAnalyticsPanel from "../components/OrgAnalyticsPanel";
import OrgViewAnalytics from "../components/OrgViewAnalytics";
import OverallAnalyticsCharts from "../components/OverallAnalyticsCharts";
import CreateOrgModal from "../components/CreateOrgModal";
import SessionEditor from "../components/SessionEditor";
import { useAuth } from "../context/AuthContext";
import React, { useState, useEffect, useCallback } from "react";
import { useLocation, Link, useHistory } from "react-router-dom";
import { debounce } from "lodash";
import axios from "axios";
import { Globe, Loader2 } from "lucide-react";
import {
  Card,
  CardBody,
  CardTitle,
  Label,
  Input,
  Button,
  Dropdown,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";

const API_URL =
  process.env.REACT_APP_API_URL || "https://api.littleexplorers.help";

const ORGANIZATION_TYPES = [
  { value: "", label: "Select type..." },
  { value: "Nonprofit", label: "Nonprofit" },
  { value: "For-profit", label: "For-profit" },
  { value: "School", label: "School" },
  { value: "Government", label: "Government" },
  { value: "Other", label: "Other" },
];

const PROVIDER_SCOPES = [
  { value: "Local", label: "Local" },
  { value: "Regional", label: "Regional" },
  { value: "National", label: "National" },
  { value: "International", label: "International" },
];

// Provider (Organization) Info View - Comprehensive Form
function ProviderInfoView({
  provider,
  handleProviderDataChange,
  handleSaveChanges,
  getAccessToken,
}) {
  return (
    <div style={{ marginTop: "20px" }}>
      {/* Basic Information Section */}
      <div className="mb-6">
        <h4 className="text-white font-medium mb-4 pb-2 border-b border-gray-700">
          Basic Information
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-gray-300" for="name">
              Organization Name
            </Label>
            <Input
              type="text"
              name="name"
              value={provider.name || ""}
              onChange={handleProviderDataChange}
              className="bg-gray-800 border-gray-600 text-white"
            />
          </div>

          <div>
            <Label className="text-gray-300" for="type">
              Type
            </Label>
            <Input
              type="select"
              name="type"
              value={provider.type || ""}
              onChange={handleProviderDataChange}
              className="bg-gray-800 border-gray-600 text-white"
            >
              {ORGANIZATION_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Input>
          </div>
        </div>

        <div className="mt-4">
          <Label className="text-gray-300" for="description">
            Description
          </Label>
          <Input
            type="textarea"
            name="description"
            value={provider.description || ""}
            onChange={handleProviderDataChange}
            rows={4}
            className="bg-gray-800 border-gray-600 text-white"
          />
          <div className="text-gray-500 text-sm mt-1">
            {(provider.description || "").length} characters (50+ recommended)
          </div>
        </div>
      </div>

      {/* Location Section */}
      <div className="mb-6">
        <h4 className="text-white font-medium mb-4 pb-2 border-b border-gray-700">
          Location
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-gray-300" for="location.city">
              City
            </Label>
            <Input
              type="text"
              name="location.city"
              value={provider.location?.city || ""}
              onChange={handleProviderDataChange}
              className="bg-gray-800 border-gray-600 text-white"
            />
          </div>

          <div>
            <Label className="text-gray-300" for="location.state">
              State
            </Label>
            <Input
              type="text"
              name="location.state"
              value={provider.location?.state || ""}
              onChange={handleProviderDataChange}
              className="bg-gray-800 border-gray-600 text-white"
            />
          </div>

          <div>
            <Label className="text-gray-300" for="location.country">
              Country
            </Label>
            <Input
              type="text"
              name="location.country"
              value={provider.location?.country || "US"}
              onChange={handleProviderDataChange}
              className="bg-gray-800 border-gray-600 text-white"
            />
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="mb-6">
        <h4 className="text-white font-medium mb-4 pb-2 border-b border-gray-700">
          Contact Information
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-gray-300" for="website">
              Website
            </Label>
            <Input
              type="text"
              name="website"
              value={provider.website || ""}
              onChange={handleProviderDataChange}
              placeholder="https://..."
              className="bg-gray-800 border-gray-600 text-white"
            />
          </div>

          <div>
            <Label className="text-gray-300" for="contactEmail">
              Contact Email
            </Label>
            <Input
              type="email"
              name="contactEmail"
              value={provider.contactEmail || ""}
              onChange={handleProviderDataChange}
              className="bg-gray-800 border-gray-600 text-white"
            />
          </div>

          <div>
            <Label className="text-gray-300" for="contactPhone">
              Contact Phone
            </Label>
            <Input
              type="text"
              name="contactPhone"
              value={provider.contactPhone || ""}
              onChange={handleProviderDataChange}
              className="bg-gray-800 border-gray-600 text-white"
            />
          </div>

          <div>
            <Label className="text-gray-300" for="providerScope">
              Provider Scope
            </Label>
            <Input
              type="select"
              name="providerScope"
              value={provider.providerScope || "Local"}
              onChange={handleProviderDataChange}
              className="bg-gray-800 border-gray-600 text-white"
            >
              {PROVIDER_SCOPES.map((scope) => (
                <option key={scope.value} value={scope.value}>
                  {scope.label}
                </option>
              ))}
            </Input>
          </div>
        </div>
      </div>

      {/* Status Toggles */}
      <div className="mb-6">
        <h4 className="text-white font-medium mb-4 pb-2 border-b border-gray-700">
          Status
        </h4>

        <div className="flex flex-wrap gap-6">
          <Label check className="flex items-center gap-2 cursor-pointer">
            <Input
              type="checkbox"
              name="isCurated"
              checked={provider.isCurated || false}
              onChange={handleProviderDataChange}
              className="w-5 h-5"
            />
            <span className="text-gray-300">Curated (featured in search)</span>
          </Label>

          <Label check className="flex items-center gap-2 cursor-pointer">
            <Input
              type="checkbox"
              name="isVerified"
              checked={provider.isVerified || false}
              onChange={handleProviderDataChange}
              className="w-5 h-5"
            />
            <span className="text-gray-300">Verified</span>
          </Label>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-6 pt-4 border-t border-gray-700">
        <Button
          color="primary"
          onClick={handleSaveChanges}
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            border: "none",
          }}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
}

// Program List View
function ProgramListView({
  programs,
  onSelectProgram,
  selectedProgramId,
  onDeleteProgram,
}) {
  if (programs.length === 0) {
    return <p style={{ color: "#9ca3af" }}>No programs found.</p>;
  }

  return (
    <div style={{ marginTop: "20px" }}>
      <h4 style={{ color: "#fff", marginBottom: "10px" }}>Programs</h4>
      <ul style={{ listStyle: "none", padding: 0 }}>
        {programs.map((program) => (
          <li
            key={program.id}
            onClick={() => onSelectProgram(program)}
            style={{
              padding: "10px",
              marginBottom: "5px",
              background:
                program.id === selectedProgramId
                  ? "rgba(102, 126, 234, 0.3)"
                  : "rgba(255, 255, 255, 0.05)",
              borderRadius: "4px",
              cursor: "pointer",
              color: "#e0e0e0",
              border:
                program.id === selectedProgramId
                  ? "1px solid #667eea"
                  : "1px solid transparent",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontWeight: "bold" }}>{program.name}</div>
              <div style={{ fontSize: "0.9em", color: "#9ca3af" }}>
                {program.categoryId} • Ages {program.minAge || "?"}-
                {program.maxAge || "?"}
              </div>
            </div>
            {onDeleteProgram && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteProgram(program);
                }}
                style={{
                  padding: "4px 8px",
                  background: "rgba(220, 53, 69, 0.2)",
                  border: "none",
                  borderRadius: "4px",
                  color: "#ff6b6b",
                  cursor: "pointer",
                  fontSize: "0.8em",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(220, 53, 69, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(220, 53, 69, 0.2)";
                }}
                onFocus={(e) => {
                  e.currentTarget.style.background = "rgba(220, 53, 69, 0.4)";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.background = "rgba(220, 53, 69, 0.2)";
                }}
              >
                Delete
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Session List View
function SessionListView({ sessions, onAddSession }) {
  if (sessions.length === 0) {
    return (
      <div>
        <p style={{ color: "#9ca3af" }}>No sessions found.</p>
        <Button onClick={onAddSession} style={{ marginTop: "10px" }}>
          Add Session
        </Button>
      </div>
    );
  }

  return (
    <div style={{ marginTop: "20px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
        }}
      >
        <h4 style={{ color: "#fff" }}>Sessions</h4>
        <Button onClick={onAddSession} size="sm">
          Add Session
        </Button>
      </div>
      <ul style={{ color: "#e0e0e0", listStyle: "none", padding: 0 }}>
        {sessions.map((session) => (
          <li
            key={session.id}
            style={{
              padding: "10px",
              marginBottom: "5px",
              background: "rgba(255, 255, 255, 0.05)",
              borderRadius: "4px",
            }}
          >
            <div style={{ fontWeight: "bold" }}>
              {session.name || "Unnamed Session"}
            </div>
            <div style={{ fontSize: "0.9em", color: "#9ca3af" }}>
              {new Date(session.startDate).toLocaleDateString()} -{" "}
              {new Date(session.endDate).toLocaleDateString()}
              {session.daysOfWeek && ` • ${session.daysOfWeek}`}
              {session.startTime &&
                ` • ${session.startTime}-${session.endTime}`}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Add Program Modal
function AddProgramModal({
  providerId,
  isOpen,
  toggle,
  onSuccess,
  getAccessToken,
}) {
  const [programData, setProgramData] = useState({
    name: "",
    description: "",
    categoryId: "",
    minAge: "",
    maxAge: "",
    sessionType: "in-person",
    registrationUrl: "",
    requiresInquiry: false,
    inquiryUrl: "",
  });

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setProgramData((prev) => ({ ...prev, [name]: value }));
  };

  const saveProgram = async () => {
    try {
      const token = getAccessToken();
      const response = await axios.post(
        `${API_URL}/v2/organizations/${providerId}/programs`,
        {
          ...programData,
          minAge: programData.minAge
            ? parseFloat(programData.minAge)
            : undefined,
          maxAge: programData.maxAge
            ? parseFloat(programData.maxAge)
            : undefined,
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      onSuccess(response.data);
      toggle();
      setProgramData({
        name: "",
        description: "",
        categoryId: "",
        minAge: "",
        maxAge: "",
        sessionType: "in-person",
        registrationUrl: "",
        requiresInquiry: false,
        inquiryUrl: "",
      });
    } catch (error) {
      console.error("Error saving program:", error);
      alert(
        "Failed to save program: " +
          (error.response?.data?.error || error.message),
      );
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} style={{ maxWidth: "700px" }}>
      <ModalHeader toggle={toggle}
        style={{
          background: "rgba(30, 30, 40, 0.95)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          color: "#fff",
        }}
      >Create a new program</ModalHeader>
      <ModalBody
        style={{
          background: "rgba(20, 20, 30, 0.95)",
          color: "#e0e0e0",
          maxHeight: "70vh",
          overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", margin: "10px 0", background: "rgba(30, 30, 40, 0.95)", padding: "10px" }}>
          <Label style={{ width: "150px" }} for="name">
            Program Name *
          </Label>
          <Input
            type="text"
            name="name"
            value={programData.name}
            onChange={handleInputChange}
            placeholder="e.g., Future Stars Soccer"
          />
        </div>
        <div style={{ display: "flex", margin: "10px 0" }}>
          <Label style={{ width: "150px" }} for="description">
            Description
          </Label>
          <Input
            type="textarea"
            name="description"
            value={programData.description}
            onChange={handleInputChange}
            placeholder="Program description"
          />
        </div>
        <div style={{ display: "flex", margin: "10px 0" }}>
          <Label style={{ width: "150px" }} for="categoryId">
            Category *
          </Label>
          <Input
            type="select"
            name="categoryId"
            value={programData.categoryId}
            onChange={handleInputChange}
          >
            <option value="">Select a category</option>
            <option value="sports">Sports</option>
            <option value="arts">Arts & Crafts</option>
            <option value="music">Music</option>
            <option value="stem">STEM</option>
            <option value="nature">Nature & Outdoors</option>
            <option value="dance">Dance</option>
            <option value="language">Language</option>
          </Input>
        </div>
        <div style={{ display: "flex", margin: "10px 0" }}>
          <Label style={{ width: "150px" }} for="minAge">
            Min Age
          </Label>
          <Input
            type="number"
            name="minAge"
            value={programData.minAge}
            onChange={handleInputChange}
            placeholder="e.g., 4"
          />
        </div>
        <div style={{ display: "flex", margin: "10px 0" }}>
          <Label style={{ width: "150px" }} for="maxAge">
            Max Age
          </Label>
          <Input
            type="number"
            name="maxAge"
            value={programData.maxAge}
            onChange={handleInputChange}
            placeholder="e.g., 12"
          />
        </div>
        <div style={{ display: "flex", margin: "10px 0" }}>
          <Label style={{ width: "150px" }} for="sessionType">
            Session Type
          </Label>
          <Input
            type="select"
            name="sessionType"
            value={programData.sessionType}
            onChange={handleInputChange}
          >
            <option value="in-person">In-Person</option>
            <option value="virtual">Virtual</option>
            <option value="hybrid">Hybrid</option>
          </Input>
        </div>
        <div style={{ display: "flex", margin: "10px 0" }}>
          <Label style={{ width: "150px" }} for="registrationUrl">
            Registration URL
          </Label>
          <Input
            type="url"
            name="registrationUrl"
            value={programData.registrationUrl}
            onChange={handleInputChange}
            placeholder="https://example.com/register"
          />
        </div>
        <div
          style={{ display: "flex", margin: "10px 0", alignItems: "center" }}
        >
          <Label style={{ width: "150px" }} for="requiresInquiry">
            Requires Inquiry
          </Label>
          <Input
            type="checkbox"
            name="requiresInquiry"
            checked={programData.requiresInquiry}
            onChange={handleInputChange}
            style={{ width: "20px", marginRight: "10px" }}
          />
          <span style={{ color: "#6c757d", fontSize: "0.9em" }}>
            Check if this program requires form submission (no regular sessions)
          </span>
        </div>
        {programData.requiresInquiry && (
          <div style={{ display: "flex", margin: "10px 0" }}>
            <Label style={{ width: "150px" }} for="inquiryUrl">
              Inquiry URL
            </Label>
            <Input
              type="url"
              name="inquiryUrl"
              value={programData.inquiryUrl}
              onChange={handleInputChange}
              placeholder="https://example.com/inquiry"
            />
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={saveProgram}>
          Save Program
        </Button>{" "}
        <Button color="secondary" onClick={toggle}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// Edit Program Modal with Session Editing
function EditProgramModal({
  program,
  isOpen,
  toggle,
  onSuccess,
  getAccessToken,
}) {
  const [programData, setProgramData] = useState({
    name: "",
    description: "",
    categoryId: "",
    minAge: "",
    maxAge: "",
    sessionType: "in-person",
    registrationUrl: "",
    requiresInquiry: false,
    inquiryUrl: "",
  });
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditingSessions, setIsEditingSessions] = useState(false);

  // Load program and session data when modal opens
  useEffect(() => {
    if (program && isOpen) {
      setProgramData({
        name: program.name || "",
        description: program.description || "",
        categoryId: program.categoryId || "",
        minAge: program.minAge || "",
        maxAge: program.maxAge || "",
        sessionType: program.sessionType || "in-person",
        registrationUrl: program.registrationUrl || "",
        requiresInquiry: program.requiresInquiry || false,
        inquiryUrl: program.inquiryUrl || "",
      });
      loadSessionsForProgram();
    }
  }, [program, isOpen]);

  const loadSessionsForProgram = async () => {
    if (!program?.id) return;
    setIsLoading(true);
    try {
      const token = getAccessToken();
      const response = await axios.get(
        `${API_URL}/v2/programs/${program.id}/sessions`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      console.log("Loaded sessions:", response.data.sessions);
      // Transform sessions to match the format expected by SessionEditor
      const transformedSessions = (response.data.sessions || []).map((s) => {
        const transformed = {
          id: s.id,
          name: s.name,
          description: s.description || "",
          startDate: s.startDate
            ? new Date(s.startDate).toISOString().split("T")[0]
            : "",
          endDate: s.endDate
            ? new Date(s.endDate).toISOString().split("T")[0]
            : "",
          daysOfWeek: s.daysOfWeek || "",
          startTime: s.startTime || "",
          endTime: s.endTime || "",
          capacity: s.capacity,
          priceTotal: s.priceTotal,
          registrationUrl: s.registrationUrl || "",
          isTryout: s.isTryout || false,
        };
        console.log("Session transformed:", s.name, "->", transformed);
        return transformed;
      });
      setSessions(transformedSessions);
    } catch (error) {
      console.error("Error loading sessions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setProgramData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Session editing functions
  const updateSessionField = (programIdx, sessionIdx, field, value) => {
    setSessions((prev) =>
      prev.map((s, i) => (i === sessionIdx ? { ...s, [field]: value } : s)),
    );
  };

  const addSession = () => {
    const newSession = {
      name: "",
      description: "",
      startDate: "",
      endDate: "",
      daysOfWeek: "",
      startTime: "",
      endTime: "",
      capacity: undefined,
      priceTotal: undefined,
      registrationUrl: "",
      isTryout: false,
    };
    setSessions((prev) => [...prev, newSession]);
  };

  const removeSession = (programIdx, sessionIdx) => {
    setSessions((prev) => prev.filter((_, i) => i !== sessionIdx));
  };

  const deleteProgram = async () => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${programData.name}" and all its sessions? This action cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      const token = getAccessToken();
      await axios.delete(`${API_URL}/v2/programs/${program.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      onSuccess();
      toggle();
      alert(
        `Program "${programData.name}" and all its sessions have been deleted.`,
      );
    } catch (error) {
      console.error("Error deleting program:", error);
      alert(
        "Failed to delete program: " +
          (error.response?.data?.error || error.message),
      );
    }
  };

  const saveChanges = async () => {
    try {
      const token = getAccessToken();

      // Save program data
      await axios.put(
        `${API_URL}/v2/programs/${program.id}`,
        {
          ...programData,
          minAge: programData.minAge
            ? Number.parseFloat(programData.minAge)
            : undefined,
          maxAge: programData.maxAge
            ? Number.parseFloat(programData.maxAge)
            : undefined,
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );

      // Save session changes
      for (const session of sessions) {
        if (session.id) {
          // Update existing session
          await axios.put(
            `${API_URL}/v2/sessions/${session.id}`,
            {
              name: session.name,
              description: session.description,
              startDate: session.startDate,
              endDate: session.endDate,
              daysOfWeek: session.daysOfWeek,
              startTime: session.startTime,
              endTime: session.endTime,
              capacity: session.capacity,
              priceTotal: session.priceTotal,
              registrationUrl: session.registrationUrl,
              isTryout: session.isTryout,
            },
            {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            },
          );
        } else {
          // Create new session
          await axios.post(
            `${API_URL}/v2/programs/${program.id}/sessions`,
            {
              name: session.name,
              description: session.description,
              startDate: session.startDate,
              endDate: session.endDate,
              daysOfWeek: session.daysOfWeek,
              startTime: session.startTime,
              endTime: session.endTime,
              capacity: session.capacity,
              priceTotal: session.priceTotal,
              registrationUrl: session.registrationUrl,
              isTryout: session.isTryout,
            },
            {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            },
          );
        }
      }

      onSuccess();
      toggle();
    } catch (error) {
      console.error("Error saving changes:", error);
      alert(
        "Failed to save changes: " +
          (error.response?.data?.error || error.message),
      );
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} style={{ maxWidth: "900px" }}>
      <ModalHeader toggle={toggle}
        style={{
          background: "rgba(30, 30, 40, 0.95)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          color: "#fff",
        }}
      >Edit Program & Sessions</ModalHeader>
      <ModalBody
        style={{
          background: "rgba(20, 20, 30, 0.95)",
          color: "#e0e0e0",
          maxHeight: "70vh",
          overflowY: "auto",
        }}
      >
        {/* Program Info Section */}
        <div
          style={{
            borderBottom: "1px solid #dee2e6",
            background: "rgba(30, 30, 40, 0.95)",
            paddingBottom: "15px",
            marginBottom: "15px",
          }}
        >
          <h5 style={{ marginBottom: "15px", color: "#495057" }}>
            Program Information
          </h5>
          <div style={{ display: "flex", margin: "10px 0" }}>
            <Label style={{ width: "150px" }} for="name">
              Program Name *
            </Label>
            <Input
              type="text"
              name="name"
              value={programData.name}
              onChange={handleInputChange}
              placeholder="e.g., Future Stars Soccer"
            />
          </div>
          <div style={{ display: "flex", margin: "10px 0" }}>
            <Label style={{ width: "150px" }} for="description">
              Description
            </Label>
            <Input
              type="textarea"
              name="description"
              value={programData.description}
              onChange={handleInputChange}
              placeholder="Program description"
            />
          </div>
          <div style={{ display: "flex", margin: "10px 0" }}>
            <Label style={{ width: "150px" }} for="categoryId">
              Category *
            </Label>
            <Input
              type="select"
              name="categoryId"
              value={programData.categoryId}
              onChange={handleInputChange}
            >
              <option value="">Select a category</option>
              <option value="sports">Sports</option>
              <option value="arts">Arts & Crafts</option>
              <option value="music">Music</option>
              <option value="stem">STEM</option>
              <option value="nature">Nature & Outdoors</option>
              <option value="dance">Dance</option>
              <option value="language">Language</option>
            </Input>
          </div>
          <div style={{ display: "flex", margin: "10px 0" }}>
            <Label style={{ width: "150px" }} for="minAge">
              Min Age
            </Label>
            <Input
              type="number"
              name="minAge"
              value={programData.minAge}
              onChange={handleInputChange}
              placeholder="e.g., 4"
            />
          </div>
          <div style={{ display: "flex", margin: "10px 0" }}>
            <Label style={{ width: "150px" }} for="maxAge">
              Max Age
            </Label>
            <Input
              type="number"
              name="maxAge"
              value={programData.maxAge}
              onChange={handleInputChange}
              placeholder="e.g., 12"
            />
          </div>
          <div style={{ display: "flex", margin: "10px 0" }}>
            <Label style={{ width: "150px" }} for="sessionType">
              Session Type
            </Label>
            <Input
              type="select"
              name="sessionType"
              value={programData.sessionType}
              onChange={handleInputChange}
            >
              <option value="in-person">In-Person</option>
              <option value="virtual">Virtual</option>
              <option value="hybrid">Hybrid</option>
            </Input>
          </div>
          <div style={{ display: "flex", margin: "10px 0" }}>
            <Label style={{ width: "150px" }} for="registrationUrl">
              Registration URL
            </Label>
            <Input
              type="url"
              name="registrationUrl"
              value={programData.registrationUrl}
              onChange={handleInputChange}
              placeholder="https://example.com/register"
            />
          </div>
          <div
            style={{ display: "flex", margin: "10px 0", alignItems: "center" }}
          >
            <Label style={{ width: "150px" }} for="requiresInquiry">
              Requires Inquiry
            </Label>
            <Input
              type="checkbox"
              name="requiresInquiry"
              checked={programData.requiresInquiry}
              onChange={handleInputChange}
              style={{ width: "20px", marginRight: "10px" }}
            />
            <span style={{ color: "#6c757d", fontSize: "0.9em" }}>
              Check if this program requires form submission (no regular
              sessions)
            </span>
          </div>
          {programData.requiresInquiry && (
            <div style={{ display: "flex", margin: "10px 0" }}>
              <Label style={{ width: "150px" }} for="inquiryUrl">
                Inquiry URL
              </Label>
              <Input
                type="url"
                name="inquiryUrl"
                value={programData.inquiryUrl}
                onChange={handleInputChange}
                placeholder="https://example.com/inquiry"
              />
            </div>
          )}
        </div>

        {/* Sessions Section */}
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <h5 style={{ color: "#495057", margin: 0 }}>
              Sessions ({sessions.length})
            </h5>
            <div style={{ display: "flex", gap: "10px" }}>
              <Button
                color="info"
                size="sm"
                onClick={() => {
                  console.log("Current sessions state:", sessions);
                  alert("Check console for session data");
                }}
              >
                Debug
              </Button>
              <Button
                color={isEditingSessions ? "secondary" : "primary"}
                size="sm"
                onClick={() => setIsEditingSessions(!isEditingSessions)}
              >
                {isEditingSessions ? "Done Editing Sessions" : "Edit Sessions"}
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Loader2 className="animate-spin" style={{ margin: "0 auto" }} />
            </div>
          ) : (
            <SessionEditor
              sessions={sessions}
              programIndex={0}
              isEditing={isEditingSessions}
              onUpdate={updateSessionField}
              onAdd={addSession}
              onRemove={removeSession}
              variant="compact"
            />
          )}

          {/* Debug: Show raw session data */}
          <details style={{ marginTop: "15px", fontSize: "0.85em" }}>
            <summary style={{ color: "#6c757d", cursor: "pointer" }}>
              View Raw Session Data (Debug)
            </summary>
            <pre
              style={{
                background: "#f8f9fa",
                padding: "10px",
                borderRadius: "4px",
                marginTop: "10px",
                maxHeight: "300px",
                overflow: "auto",
                fontSize: "0.8em",
              }}
            >
              {JSON.stringify(sessions, null, 2)}
            </pre>
          </details>
        </div>
      </ModalBody>
      <ModalFooter style={{ display: "flex", justifyContent: "space-between" }}>
        <Button color="danger" onClick={deleteProgram}>
          Delete Program
        </Button>
        <div>
          <Button color="primary" onClick={saveChanges}>
            Save All Changes
          </Button>{" "}
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}

// Add Session Modal
function AddSessionModal({
  programId,
  isOpen,
  toggle,
  onSuccess,
  getAccessToken,
}) {
  const [sessionData, setSessionData] = useState({
    name: "",
    startDate: "",
    endDate: "",
    daysOfWeek: "",
    startTime: "",
    endTime: "",
    capacity: "",
    priceTotal: "",
  });

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setSessionData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const saveSession = async () => {
    try {
      const token = getAccessToken();
      const response = await axios.post(
        `${API_URL}/v2/programs/${programId}/sessions`,
        {
          ...sessionData,
          capacity: sessionData.capacity
            ? parseInt(sessionData.capacity, 10)
            : undefined,
          priceTotal: sessionData.priceTotal
            ? parseFloat(sessionData.priceTotal)
            : undefined,
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      onSuccess(response.data);
      toggle();
      setSessionData({
        name: "",
        startDate: "",
        endDate: "",
        daysOfWeek: "",
        startTime: "",
        endTime: "",
        capacity: "",
        priceTotal: "",
      });
    } catch (error) {
      console.error("Error saving session:", error);
      alert(
        "Failed to save session: " +
          (error.response?.data?.error || error.message),
      );
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} style={{ maxWidth: "700px" }}>
      <ModalHeader toggle={toggle}
        style={{
          background: "rgba(30, 30, 40, 0.95)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          color: "#fff",
        }}
      >Create a new session</ModalHeader>
      <ModalBody
        style={{
          background: "rgba(20, 20, 30, 0.95)",
          color: "#e0e0e0",
          maxHeight: "70vh",
          overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", margin: "10px 0", background: "rgba(30, 30, 40, 0.95)" }}>
          <Label style={{ width: "150px" }} for="name">
            Session Name
          </Label>
          <Input
            type="text"
            name="name"
            value={sessionData.name}
            onChange={handleInputChange}
            placeholder="e.g., Spring 2024 Session"
          />
        </div>
        <div style={{ display: "flex", margin: "10px 0" }}>
          <Label style={{ width: "150px" }} for="startDate">
            Start Date *
          </Label>
          <Input
            type="date"
            name="startDate"
            value={sessionData.startDate}
            onChange={handleInputChange}
          />
        </div>
        <div style={{ display: "flex", margin: "10px 0" }}>
          <Label style={{ width: "150px" }} for="endDate">
            End Date *
          </Label>
          <Input
            type="date"
            name="endDate"
            value={sessionData.endDate}
            onChange={handleInputChange}
          />
        </div>
        <div style={{ display: "flex", margin: "10px 0" }}>
          <Label style={{ width: "150px" }} for="daysOfWeek">
            Days of Week
          </Label>
          <Input
            type="text"
            name="daysOfWeek"
            value={sessionData.daysOfWeek}
            onChange={handleInputChange}
            placeholder="e.g., Mon/Wed/Fri"
          />
        </div>
        <div style={{ display: "flex", margin: "10px 0" }}>
          <Label style={{ width: "150px" }} for="startTime">
            Start Time
          </Label>
          <Input
            type="time"
            name="startTime"
            value={sessionData.startTime}
            onChange={handleInputChange}
          />
        </div>
        <div style={{ display: "flex", margin: "10px 0" }}>
          <Label style={{ width: "150px" }} for="endTime">
            End Time
          </Label>
          <Input
            type="time"
            name="endTime"
            value={sessionData.endTime}
            onChange={handleInputChange}
          />
        </div>
        <div style={{ display: "flex", margin: "10px 0" }}>
          <Label style={{ width: "150px" }} for="capacity">
            Capacity
          </Label>
          <Input
            type="number"
            name="capacity"
            value={sessionData.capacity}
            onChange={handleInputChange}
            placeholder="e.g., 20"
          />
        </div>
        <div style={{ display: "flex", margin: "10px 0" }}>
          <Label style={{ width: "150px" }} for="priceTotal">
            Price ($)
          </Label>
          <Input
            type="number"
            step="0.01"
            name="priceTotal"
            value={sessionData.priceTotal}
            onChange={handleInputChange}
            placeholder="e.g., 150.00"
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={saveSession}>
          Save Session
        </Button>{" "}
        <Button color="secondary" onClick={toggle}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// Provider Detail View - Shows Provider Info, Programs, and Sessions
function ProviderDetailView({ provider, getAccessToken }) {
  const [activePanel, setActivePanel] = useState("info");
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isAddProgramModalOpen, setIsAddProgramModalOpen] = useState(false);
  const [isEditProgramModalOpen, setIsEditProgramModalOpen] = useState(false);
  const [isAddSessionModalOpen, setIsAddSessionModalOpen] = useState(false);
  const [editedProvider, setEditedProvider] = useState(provider);
  const [isIngesting, setIsIngesting] = useState(false);
  const [ingestionMessage, setIngestionMessage] = useState("");

  // Claim management state
  const [claimHistory, setClaimHistory] = useState([]);
  const [loadingClaims, setLoadingClaims] = useState(false);
  const [assignEmail, setAssignEmail] = useState("");
  const [assignNotes, setAssignNotes] = useState("");
  const [assignMessage, setAssignMessage] = useState(null);

  // Reset editedProvider when provider changes
  useEffect(() => {
    setEditedProvider(provider);
  }, [provider]);

  // Load programs when provider changes
  useEffect(() => {
    if (provider?.id) {
      loadPrograms();
    }
  }, [provider?.id]);

  // Load sessions when selected program changes
  useEffect(() => {
    if (selectedProgram?.id) {
      loadSessions(selectedProgram.id);
    } else {
      setSessions([]);
    }
  }, [selectedProgram?.id]);

  const loadPrograms = async () => {
    try {
      const token = getAccessToken();
      const response = await axios.get(
        `${API_URL}/v2/organizations/${provider.id}/programs`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      setPrograms(response.data.programs || []);
    } catch (error) {
      console.error("Error loading programs:", error);
    }
  };

  const loadSessions = async (programId) => {
    try {
      const token = getAccessToken();
      const response = await axios.get(
        `${API_URL}/v2/programs/${programId}/sessions`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      console.log(
        "Sessions loaded for program",
        programId,
        ":",
        response.data.sessions,
      );
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error("Error loading sessions:", error);
    }
  };

  // Load claim history when provider changes
  useEffect(() => {
    if (provider?.id) {
      loadClaimHistory();
    }
  }, [provider?.id]);

  const loadClaimHistory = async () => {
    try {
      setLoadingClaims(true);
      const token = getAccessToken();
      const response = await axios.get(
        `${API_URL}/admin/organizations/${provider.id}/claim-history`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      setClaimHistory(response.data.history || []);
    } catch (error) {
      console.error("Error loading claim history:", error);
    } finally {
      setLoadingClaims(false);
    }
  };

  const handleAssignOrganization = async () => {
    if (!assignEmail) {
      setAssignMessage({ type: "error", text: "Email is required" });
      return;
    }

    try {
      const token = getAccessToken();
      const response = await axios.post(
        `${API_URL}/admin/organizations/${provider.id}/assign`,
        { email: assignEmail, notes: assignNotes },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );

      if (response.data.success) {
        setAssignMessage({
          type: "success",
          text:
            response.data.assignmentType === "immediate"
              ? `Assigned to existing user ${assignEmail}`
              : `Reserved for ${assignEmail}. Will auto-claim on signup.`,
        });
        setAssignEmail("");
        setAssignNotes("");
        loadClaimHistory(); // Refresh history
      }
    } catch (error) {
      setAssignMessage({
        type: "error",
        text: error.response?.data?.error || "Failed to assign",
      });
    }
  };

  const handleReleaseOrganization = async () => {
    if (
      !window.confirm(
        "Are you sure you want to release this organization? It will become unclaimed.",
      )
    ) {
      return;
    }

    try {
      const token = getAccessToken();
      await axios.post(
        `${API_URL}/admin/organizations/${provider.id}/release`,
        { reason: "Released via admin panel" },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );

      setAssignMessage({
        type: "success",
        text: "Organization released successfully",
      });
      loadClaimHistory(); // Refresh history
    } catch (error) {
      setAssignMessage({
        type: "error",
        text: error.response?.data?.error || "Failed to release",
      });
    }
  };

  const handleProviderDataChange = (event) => {
    const { name, value, type, checked } = event.target;
    setEditedProvider((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSaveProvider = async () => {
    try {
      const token = getAccessToken();
      const response = await axios.put(
        `${API_URL}/v2/organizations/${provider.id}`,
        {
          name: editedProvider.name,
          type: editedProvider.type,
          description: editedProvider.description,
          website: editedProvider.website,
          city: editedProvider.location?.city,
          state: editedProvider.location?.state,
          country: editedProvider.location?.country,
          isCurated: editedProvider.isCurated,
          isVerified: editedProvider.isVerified,
          contactPhone: editedProvider.contactPhone,
          contactEmail: editedProvider.contactEmail,
          providerScope: editedProvider.providerScope,
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );

      // Update the provider data with the response
      setEditedProvider(response.data);
      alert("Organization updated successfully!");
    } catch (error) {
      console.error("Error saving provider:", error);
      alert(
        "Failed to update organization: " +
          (error.response?.data?.error || error.message),
      );
    }
  };

  const handleSelectProgram = (program) => {
    setSelectedProgram(program);
  };

  const handleDeleteProgram = async (program) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${program.name}" and all its sessions? This action cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      const token = getAccessToken();
      await axios.delete(`${API_URL}/v2/programs/${program.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      // Reload programs and clear selection if the deleted program was selected
      loadPrograms();
      if (selectedProgram?.id === program.id) {
        setSelectedProgram(null);
      }
      alert(
        `Program "${program.name}" and all its sessions have been deleted.`,
      );
    } catch (error) {
      console.error("Error deleting program:", error);
      alert(
        "Failed to delete program: " +
          (error.response?.data?.error || error.message),
      );
    }
  };

  const handleIngestFromWebsite = async () => {
    if (!provider.website) {
      setIngestionMessage("No website URL available for this organization");
      return;
    }

    try {
      setIsIngesting(true);
      setIngestionMessage("");

      const token = getAccessToken();
      const response = await fetch(`${API_URL}/v2/ingestion/queue`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          url: provider.website,
          organizationId: provider.id,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to queue ingestion");
      }

      const data = await response.json();
      setIngestionMessage(`Ingestion queued successfully! Job ID: ${data.id}`);
    } catch (error) {
      console.error("Error queuing ingestion:", error);
      setIngestionMessage(`Error: ${error.message}`);
    } finally {
      setIsIngesting(false);
    }
  };

  return (
    <div style={{ flex: "1 0 25%", marginTop: "20px" }}>
      <Card
        style={{
          background: "rgba(255, 255, 255, 0.03)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <CardBody>
          <CardTitle>
            <h3 style={{ color: "#fff" }}>Provider: {provider.name}</h3>
          </CardTitle>
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              onClick={() => setActivePanel("programs")}
              style={{
                backgroundColor:
                  activePanel === "programs" ? "#667eea" : "#6b7280",
              }}
            >
              Programs ({programs.length})
            </Button>
            <Button
              onClick={() => setActivePanel("claims")}
              style={{
                backgroundColor:
                  activePanel === "claims" ? "#667eea" : "#6b7280",
              }}
            >
              Claim Management
            </Button>
            {provider.website && (
              <Button
                onClick={handleIngestFromWebsite}
                disabled={isIngesting}
                style={{
                  backgroundColor: "#059669",
                  opacity: isIngesting ? 0.7 : 1,
                }}
              >
                {isIngesting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Globe className="w-4 h-4 inline mr-2" />
                    Ingest from Website
                  </>
                )}
              </Button>
            )}
          </div>

          {ingestionMessage && (
            <div
              className={`p-3 rounded-lg mb-4 text-sm ${
                ingestionMessage.includes("Error")
                  ? "bg-red-500/20 text-red-400"
                  : "bg-green-500/20 text-green-400"
              }`}
            >
              {ingestionMessage}
              {!ingestionMessage.includes("Error") && (
                <div className="mt-1">
                  <Link
                    to="/ingestion"
                    className="text-indigo-400 hover:text-indigo-300 underline"
                  >
                    View in Ingestion Queue →
                  </Link>
                </div>
              )}
            </div>
          )}

          {activePanel === "info" && (
            <>
              <div style={{ marginTop: "20px", marginBottom: "30px" }}>
                <OrgAnalyticsPanel
                  orgId={provider.id}
                  getAccessToken={getAccessToken}
                />
              </div>
              <div
                style={{
                  marginTop: "20px",
                  marginBottom: "30px",
                  paddingTop: "20px",
                  borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <OrgViewAnalytics
                  orgId={provider.id}
                  getAccessToken={getAccessToken}
                />
              </div>
              <div
                style={{
                  marginTop: "30px",
                  paddingTop: "20px",
                  borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <h4 className="text-white font-medium mb-4">
                  Edit Organization Details
                </h4>
                <ProviderInfoView
                  provider={editedProvider}
                  handleProviderDataChange={handleProviderDataChange}
                  handleSaveChanges={handleSaveProvider}
                  getAccessToken={getAccessToken}
                />
              </div>
            </>
          )}

          {activePanel === "programs" && (
            <>
              <div style={{ marginTop: "20px" }}>
                <Button onClick={() => setIsAddProgramModalOpen(true)}>
                  Add Program
                </Button>
              </div>

              <div style={{ display: "flex", gap: "20px", marginTop: "20px" }}>
                <div style={{ flex: 1 }}>
                  <ProgramListView
                    programs={programs}
                    onSelectProgram={handleSelectProgram}
                    selectedProgramId={selectedProgram?.id}
                    onDeleteProgram={handleDeleteProgram}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  {selectedProgram && (
                    <>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "10px",
                        }}
                      >
                        <h4 style={{ color: "#fff" }}>
                          Selected: {selectedProgram.name}
                        </h4>
                        <Button
                          size="sm"
                          onClick={() => setIsEditProgramModalOpen(true)}
                          style={{ marginLeft: "10px" }}
                        >
                          Edit Program
                        </Button>
                      </div>
                      <SessionListView
                        sessions={sessions}
                        onAddSession={() => setIsAddSessionModalOpen(true)}
                      />
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          {activePanel === "claims" && (
            <>
              <div style={{ marginTop: "20px" }}>
                <h4 className="text-white font-medium mb-4">
                  Organization Claim Management
                </h4>

                {/* Assignment Form */}
                <div
                  style={{
                    background: "rgba(102, 126, 234, 0.1)",
                    padding: "20px",
                    borderRadius: "8px",
                    marginBottom: "30px",
                  }}
                >
                  <h5 className="text-white font-medium mb-3">
                    Assign to User
                  </h5>

                  {assignMessage && (
                    <div
                      className={`p-3 rounded-lg mb-4 text-sm ${
                        assignMessage.type === "error"
                          ? "bg-red-500/20 text-red-400"
                          : "bg-green-500/20 text-green-400"
                      }`}
                    >
                      {assignMessage.text}
                      <button
                        onClick={() => setAssignMessage(null)}
                        className="ml-2 text-sm underline"
                      >
                        Dismiss
                      </button>
                    </div>
                  )}

                  <div className="mb-3">
                    <Label className="text-gray-300" for="assignEmail">
                      Email Address *
                    </Label>
                    <Input
                      type="email"
                      id="assignEmail"
                      value={assignEmail}
                      onChange={(e) => setAssignEmail(e.target.value)}
                      placeholder="user@example.com"
                      className="bg-gray-800 border-gray-600 text-white"
                    />
                    <small className="text-gray-500">
                      If user exists, immediate assignment. If not, reserved for
                      signup.
                    </small>
                  </div>

                  <div className="mb-3">
                    <Label className="text-gray-300" for="assignNotes">
                      Notes (optional)
                    </Label>
                    <Input
                      type="textarea"
                      id="assignNotes"
                      value={assignNotes}
                      onChange={(e) => setAssignNotes(e.target.value)}
                      placeholder="Reason for assignment..."
                      rows={2}
                      className="bg-gray-800 border-gray-600 text-white"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={handleAssignOrganization}
                      style={{ backgroundColor: "#667eea" }}
                    >
                      Assign Organization
                    </Button>
                    <Button
                      onClick={handleReleaseOrganization}
                      style={{ backgroundColor: "#dc2626" }}
                    >
                      Release/Unclaim
                    </Button>
                  </div>
                </div>

                {/* Claim History */}
                <div>
                  <h5 className="text-white font-medium mb-3">Claim History</h5>

                  {loadingClaims ? (
                    <div className="text-gray-400">Loading...</div>
                  ) : claimHistory.length === 0 ? (
                    <div className="text-gray-500">No claim history found</div>
                  ) : (
                    <div className="space-y-2">
                      {claimHistory.map((history) => (
                        <div
                          key={history.id}
                          className="p-3 rounded-lg bg-gray-800/50 border border-gray-700"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="text-white font-medium capitalize">
                                {history.claimType}
                              </span>
                              {history.assignedToEmail && (
                                <span className="text-gray-400 ml-2">
                                  to {history.assignedToEmail}
                                </span>
                              )}
                              {history.reservedForEmail && (
                                <span className="text-blue-400 ml-2">
                                  reserved for {history.reservedForEmail}
                                </span>
                              )}
                            </div>
                            <span className="text-gray-500 text-sm">
                              {new Date(
                                history.assignedAt,
                              ).toLocaleDateString()}
                            </span>
                          </div>
                          {history.notes && (
                            <div className="text-gray-500 text-sm mt-1">
                              {history.notes}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      <AddProgramModal
        providerId={provider.id}
        isOpen={isAddProgramModalOpen}
        toggle={() => setIsAddProgramModalOpen(!isAddProgramModalOpen)}
        onSuccess={() => {
          loadPrograms();
        }}
        getAccessToken={getAccessToken}
      />

      {selectedProgram && (
        <EditProgramModal
          program={selectedProgram}
          isOpen={isEditProgramModalOpen}
          toggle={() => setIsEditProgramModalOpen(!isEditProgramModalOpen)}
          onSuccess={() => {
            loadPrograms();
            // Update selected program with new data
            const updatedProgram = programs.find(
              (p) => p.id === selectedProgram.id,
            );
            if (updatedProgram) {
              setSelectedProgram(updatedProgram);
            }
          }}
          getAccessToken={getAccessToken}
        />
      )}

      {selectedProgram && (
        <AddSessionModal
          programId={selectedProgram.id}
          isOpen={isAddSessionModalOpen}
          toggle={() => setIsAddSessionModalOpen(!isAddSessionModalOpen)}
          onSuccess={() => {
            loadSessions(selectedProgram.id);
          }}
          getAccessToken={getAccessToken}
        />
      )}
    </div>
  );
}

// Main Org Management Page
const OrgManagementPage = () => {
  const { user, getAccessToken } = useAuth();
  const location = useLocation();
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoadingOrg, setIsLoadingOrg] = useState(false);
  const searchInputRef = React.useRef(null);

  // Handle selecting an organization - updates state and URL
  const handleSelectOrganization = (org) => {
    setSelectedProvider(org);
    setSearchResults([]);
    setSearchTerm(org.name);
    setHasSearched(false);
    searchInputRef.current?.blur();
    // Update URL with orgId
    history.replace(`/org-management?orgId=${org.id}`);
  };

  // Clear selected organization
  const handleClearOrganization = () => {
    setSelectedProvider(null);
    setSearchTerm("");
    history.replace("/org-management");
  };

  // Load organization from URL query param on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orgId = params.get("orgId");

    if (orgId) {
      loadOrganizationById(orgId);
    }
  }, [location.search]);

  const loadOrganizationById = async (orgId) => {
    try {
      setIsLoadingOrg(true);
      const token = getAccessToken();
      const response = await axios.get(`${API_URL}/v2/organizations/${orgId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (response.data) {
        setSelectedProvider(response.data);
        setSearchTerm(response.data.name);
      }
    } catch (error) {
      console.error("Error loading organization:", error);
    } finally {
      setIsLoadingOrg(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce(async (term) => {
      if (!term || term.length < 2) {
        setSearchResults([]);
        return;
      }
      try {
        const token = getAccessToken();
        const response = await axios.get(
          `${API_URL}/v2/organizations/search?name=${encodeURIComponent(
            term,
          )}&limit=20`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );
        setSearchResults(response.data.organizations || []);
      } catch (error) {
        console.error("Error searching providers:", error);
        setSearchResults([]);
      }
    }, 300),
    [getAccessToken],
  );

  const handleSearch = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    setHasSearched(true);
    debouncedSearch(term);
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white">Provider Management</h1>
        <p className="text-gray-400 mt-1">
          Search and manage providers, programs, and sessions
        </p>
      </div>

      <div className="glass-card p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-white font-medium">Search Providers</h3>
          <Button
            color="primary"
            onClick={() => setIsCreateModalOpen(true)}
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              border: "none",
            }}
          >
            + Create New Organization
          </Button>
        </div>
        <Input
          type="text"
          placeholder="Search providers by name..."
          value={searchTerm}
          onChange={handleSearch}
          className="futuristic-input mb-3"
          innerRef={searchInputRef}
        />
        <Dropdown
          isOpen={
            searchResults.length > 0 ||
            (hasSearched &&
              searchTerm.length >= 2 &&
              searchResults.length === 0)
          }
          toggle={() => setSearchResults([])}
        >
          <DropdownMenu
            style={{
              background: "rgba(20, 20, 20, 0.95)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            {searchResults.length > 0 ? (
              searchResults.map((result) => (
                <DropdownItem
                  key={result.id}
                  onClick={() => handleSelectOrganization(result)}
                  style={{ color: "#e0e0e0" }}
                >
                  {result.name} ({result.location?.city},{" "}
                  {result.location?.state})
                </DropdownItem>
              ))
            ) : hasSearched && searchTerm.length >= 2 ? (
              <>
                <DropdownItem
                  disabled
                  style={{ color: "#9ca3af", fontStyle: "italic" }}
                >
                  No organizations found matching "{searchTerm}"
                </DropdownItem>
                <DropdownItem
                  divider
                  style={{ borderColor: "rgba(255, 255, 255, 0.1)" }}
                />
                <DropdownItem
                  onClick={() => {
                    setIsCreateModalOpen(true);
                    setSearchResults([]);
                    searchInputRef.current?.blur();
                  }}
                  style={{ color: "#667eea", fontWeight: "bold" }}
                >
                  + Create New Organization
                </DropdownItem>
              </>
            ) : null}
          </DropdownMenu>
        </Dropdown>

        {selectedProvider && (
          <ProviderDetailView
            provider={selectedProvider}
            getAccessToken={getAccessToken}
          />
        )}
        {!selectedProvider && (
          <div className="mt-6">
            <OverallAnalyticsCharts getAccessToken={getAccessToken} />
          </div>
        )}
      </div>

      <CreateOrgModal
        isOpen={isCreateModalOpen}
        toggle={() => setIsCreateModalOpen(!isCreateModalOpen)}
        onSuccess={(newOrg) => {
          setSelectedProvider(newOrg);
          setSearchTerm(newOrg.name);
          // Update URL with new org ID
          history.replace(`/org-management?orgId=${newOrg.id}`);
        }}
        getAccessToken={getAccessToken}
      />
    </div>
  );
};

export default OrgManagementPage;
